<script setup lang="ts">
  import { Button } from '@/components/ui/button'
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
  } from '@/components/ui/command'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { CheckIcon, ChevronsUpDown } from 'lucide-vue-next'
  import { useTranslation } from '@/composables/useTranslation'
  import { type RuleExpression, useField } from 'vee-validate'
  import { type Component, computed, type PropType, ref, watch } from 'vue'

  const { t } = useTranslation({
    en: {
      selectOption: 'Select {label}...',
      search: 'Search {label}...',
      unselectedOption: 'None',
      noResults: 'No results found.'
    },
    de: {
      selectOption: '{label} auswählen...',
      search: '{label} suchen...',
      unselectedOption: 'Keine',
      noResults: 'Keine Ergebnisse gefunden.'
    },
    fr: {
      selectOption: 'Sélectionner {label}...',
      search: 'Rechercher {label}...',
      unselectedOption: 'Aucun',
      noResults: 'Aucun résultat trouvé.'
    }
  })

  export interface Option {
    value: string | number | null
    title: string
    icon?: Component
  }

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    modelValue: {
      type: [String, Number, null],
      required: false
    },
    rules: {
      type: Object as PropType<RuleExpression<any> | undefined>,
      required: false
    },
    isFilterable: {
      type: Boolean,
      required: false,
      default: false
    },
    options: {
      type: Array as PropType<Option[]>,
      required: true
    },
    inputAction: {
      type: Function,
      required: false,
      default: (_newVal: string, _oldVal: string) => {},
      validator: (fn) => {
        return typeof fn === 'function' && fn.length === 2
      }
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    selectOptionText: {
      type: String,
      required: false
    },
    allowUnselect: {
      type: Boolean,
      required: false,
      default: false
    },
    unselectedOptionText: {
      type: String,
      required: false
    },
    error: {
      type: String,
      required: false
    },
    asterisk: {
      type: Boolean,
      required: false,
      default: false
    },
    aiSource: {
      type: Boolean,
      required: false,
      default: false
    },
    variant: {
      type: String,
      required: false,
      default: 'default',
      validator: (value: string) => ['default', 'inline-text'].includes(value)
    },
    description: {
      type: String,
      required: false
    },
    popoverAlign: {
      type: String,
      required: false,
      default: undefined
    },
    limitPopoverWidth: {
      type: Boolean,
      required: false,
      default: true
    }
  })

  const emit = defineEmits(['update:modelValue', 'blur', 'focus', 'validation-state'])

  const touched = ref(false)
  const opened = ref(false)
  const selectFieldRef = ref(null)
  const filterQuery = ref('')
  const commandValue = ref('')

  const {
    value: model,
    errorMessage: internalErrorMessage,
    handleBlur,
    meta
  } = useField(props.name, props.rules, {
    syncVModel: true
  })

  const computedOptions = computed(() => {
    const unselectedOption: Option = {
      value: '',
      title: props.unselectedOptionText || t('unselectedOption')
    }
    return props.allowUnselect ? [unselectedOption, ...props.options] : props.options
  })

  const errorMessage = computed(() => {
    if (props.error) {
      return props.error
    }
    return touched.value ? internalErrorMessage.value : ''
  })

  const selectedOption = computed<Option | null>(() => {
    return computedOptions.value?.find((option) => option.value === model.value) ?? null
  })

  const filteredOptions = computed(() => {
    return (
      computedOptions.value?.filter((option) =>
        option.title.toLowerCase().includes(filterQuery.value.toLowerCase())
      ) || []
    )
  })

  const isSelectedOption = (option: Option, index: number) => {
    if (model.value !== option.value) return false
    // If there are duplicate values, only highlight the first one
    const firstMatchIndex = filteredOptions.value.findIndex((opt) => opt.value === option.value)
    return firstMatchIndex === index
  }

  const buttonVariantClasses = computed(() => {
    if (props.variant === 'inline-text') {
      return {
        base: 'border-transparent bg-transparent hover:bg-gray-50 focus-visible:ring-1 focus-visible:ring-purple-500 focus-visible:ring-offset-0 px-1 py-0 h-auto min-h-0',
        error: '!border-transparent !ring-purple-500 !bg-red-50',
        disabled: 'cursor-not-allowed bg-transparent hover:bg-transparent'
      }
    }

    // Default variant
    return {
      base: 'border-gray-200 bg-white focus-visible:ring-purple-500 focus-visible:ring-2',
      error: '!border-red-500 !ring-red-500',
      disabled: 'cursor-not-allowed bg-[#f9f9f9]'
    }
  })

  const handleOptionClick = (option: Option) => {
    filterQuery.value = ''
    model.value = option.value
    opened.value = false
    touched.value = true
    emit('update:modelValue', option.value)
  }

  const handleBlurEvent = (event: FocusEvent) => {
    filterQuery.value = ''
    touched.value = true
    handleBlur(event)
    emit('validation-state', {
      errorMessage: internalErrorMessage.value,
      isInvalid: !!errorMessage.value
    })
    emit('blur', event)
  }

  const handleFocusEvent = (event: FocusEvent) => {
    touched.value = true
    emit('focus', event)
  }

  watch(filterQuery, (newVal, oldVal) => {
    props.inputAction(newVal, oldVal)
  })

  watch(opened, (newValue) => {
    if (newValue) {
      // Reset command value when opening to prevent auto-highlight
      commandValue.value = ''
    }
  })
</script>

<template>
  <div>
    <label
      v-if="label"
      class="mb-0 block text-sm font-medium"
    >
      {{ label }}
      <span
        v-if="asterisk"
        class="text-base font-normal text-red-500"
      >
        *
      </span>
      <span
        v-if="aiSource"
        class="inline-flex h-5 w-5 items-center rounded bg-purple-200 p-1 text-xs font-semibold text-purple-700"
      >
        KI
      </span>
      <span
        v-if="$slots['label-suffix']"
        class="ml-2 inline-flex items-center align-middle"
      >
        <slot name="label-suffix"></slot>
      </span>
    </label>
    <p
      v-if="description"
      class="mb-3 text-xs text-gray-500"
    >
      {{ description }}
    </p>
    <div
      ref="selectFieldRef"
      class="relative"
    >
      <Popover
        v-model:open="opened"
        class="w-full"
      >
        <PopoverTrigger as-child>
          <Button
            variant="secondary"
            role="combobox"
            :aria-expanded="opened"
            :disabled="disabled"
            :class="[
              variant === 'inline-text' ? 'inline-flex items-center gap-1' : 'w-full',
              'justify-between truncate',
              buttonVariantClasses.base,
              errorMessage ? buttonVariantClasses.error : '',
              disabled ? buttonVariantClasses.disabled : 'cursor-pointer'
            ]"
            :data-cy="`select-${name}`"
            @blur="handleBlurEvent"
            @focus="handleFocusEvent"
          >
            <slot
              name="button-contents"
              :selected-option="selectedOption"
            >
              <div class="flex min-w-0 items-center gap-2">
                <slot name="prefix-icon"></slot>
                <component
                  :is="selectedOption?.icon"
                  v-if="selectedOption !== null && selectedOption.icon"
                  class="size-4"
                />
                <span
                  class="truncate"
                  :class="{
                    'text-gray-800': selectedOption !== null,
                    'text-gray-500': selectedOption === null
                  }"
                >
                  {{
                    selectedOption
                      ? selectedOption.title
                      : selectOptionText || t('selectOption', { label: label })
                  }}
                </span>
              </div>
            </slot>
            <ChevronsUpDown
              :class="[
                'shrink-0',
                variant === 'inline-text' ? 'ml-1 h-3 w-3 opacity-40' : 'ml-2 h-4 w-4 opacity-50'
              ]"
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          :align="popoverAlign"
          :class="[limitPopoverWidth ? 'w-[--radix-popover-trigger-width]' : '', 'p-0']"
        >
          <Command
            v-model="commandValue"
            :should-filter="false"
          >
            <CommandInput
              v-if="isFilterable"
              v-model="filterQuery"
              :class="[
                'my-2 h-8 rounded-lg border-gray-200 px-3 py-2 text-sm focus:border-purple-500 focus:ring-purple-500',
                disabled ? 'cursor-not-allowed bg-[#f9f9f9]' : 'cursor-pointer bg-white'
              ]"
              :placeholder="t('search', { label: label })"
              :disabled="disabled"
            />
            <CommandEmpty>{{ t('noResults') }}</CommandEmpty>
            <CommandList>
              <CommandGroup>
                <CommandItem
                  v-for="(option, index) in filteredOptions"
                  :key="`${option.value}-${index}`"
                  :value="`${option.title || ''}-${index}`"
                  :disabled="disabled"
                  :class="[{ 'selected-option !bg-accent': isSelectedOption(option, index) }]"
                  @select="
                    () => {
                      handleOptionClick(option)
                    }
                  "
                >
                  <slot
                    name="option-contents"
                    :option="option"
                  >
                    <div class="flex w-full items-center justify-between">
                      <div class="flex items-center gap-2">
                        <component
                          :is="option.icon"
                          v-if="option.icon"
                          class="size-4"
                        />
                        <span
                          :class="{
                            'text-gray-500': option.value === null,
                            'text-gray-800': option.value !== null
                          }"
                        >
                          {{ option.title }}
                        </span>
                      </div>
                      <CheckIcon
                        v-if="isSelectedOption(option, index)"
                        class="h-4 w-4 text-gray-600"
                      />
                    </div>
                  </slot>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
    <p
      v-if="errorMessage"
      class="mt-1 text-xs font-medium text-destructive"
      :class="{ 'opacity-50': disabled }"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>

<style scoped>
  [data-radix-popper-content-wrapper] {
    z-index: 99 !important;
  }

  /* Prevent auto-highlight of first item on open, but allow hover/keyboard navigation */
  :deep([role='option'][data-highlighted]:not(:hover):not(.selected-option)) {
    background-color: transparent !important;
  }

  /* Ensure hover still works */
  :deep([role='option']:hover) {
    background-color: hsl(var(--accent)) !important;
  }

  /* Ensure selected option always shows accent background */
  :deep([role='option'].selected-option) {
    background-color: hsl(var(--accent)) !important;
  }
  label {
    padding-left: 0 !important;
  }
</style>
